import * as yup from 'yup';
import { PensionDTO, PensionPlanTypeEnum } from '../../tables/Pensions/Pensions.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { getCustomBucketIndex } from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { OwnerModelDTO, OwnerModelFormDTO, OwnerTypeDTO } from '../../OtherAssets.meta';

export interface PensionFormValues {
  name: string;
  recipient: string | null;
  type: PensionPlanTypeEnum | null;
  lumpSumAmount: number | null;
  monthlyPayment: number | null;
  distributionAge: string | null;
  isInflationAdjusted: boolean;
  survivorBenefit: number | null;
  inflation: number | null;
  isSyncAssetValueEnabled: boolean;
  isLiquid: boolean;
  includeInGrossEstate: boolean | null;
  customBucketIndex: number | null;
  orionManagementStyleId: number | null;
  ownerModel: OwnerModelFormDTO[];
}

export interface PensionFormDTO {
  Name: string;
  Recipient: string;
  PensionPlanType: PensionPlanTypeEnum | null;
  LumpSumAmount: number | null;
  MonthlyPayment: number | null;
  DistributionAge: string | null;
  DistributionAtRetirement: boolean;
  IsInflationAdjusted: boolean;
  SurvivorBenefit: number | null;
  Inflation: number | null;
  SyncAssetValueEnabled: boolean;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean | null;
  CustomBucketIndex: number | null;
  OrionManagementStyleId: number | null;
  OwnerModel: OwnerModelDTO[];
}

export interface UpsertPensionDTO extends PensionFormDTO {
  clientId: string;
  Id: string | null;
}

interface DataToForm {
  data: PensionDTO | null;
  options: OtherAssetsDTO['Options'];
}

export const dataToForm = ({ data, options }: DataToForm): PensionFormValues =>
  data
    ? {
        name: data.Name,
        recipient: data.Recipient,
        type: data.PensionPlanType,
        lumpSumAmount: data.LumpSumAmount,
        monthlyPayment: data.MonthlyPayment,
        distributionAge: data.DistributionAge
          ? data.DistributionAge.toString()
          : data.DistributionAtRetirement
          ? 'At Retirement'
          : null,
        isInflationAdjusted: data.IsInflationAdjusted || false,
        survivorBenefit: data.SurvivorBenefit,
        inflation: data.Inflation,
        isSyncAssetValueEnabled: data.SyncAssetValueEnabled || false,
        isLiquid: data.IsLiquid,
        includeInGrossEstate: data.IncludeInGrossEstate,
        customBucketIndex: getCustomBucketIndex({
          orionManagementStyleId: data.OrionManagementStyleId,
          customBucketIndex: data.CustomBucketIndex,
          managementStyles: options.ManagementStyles,
          isExcludedFromAssetBucketing: false,
          assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
        }),
        orionManagementStyleId: data.OrionManagementStyleId,
        ownerModel: data.OwnerModel.map(owner => ({
          ...owner,
          Percentage: (owner.Percentage * 100).toFixed(2),
        })),
      }
    : {
        name: '',
        recipient: '',
        type: null,
        lumpSumAmount: null,
        monthlyPayment: null,
        distributionAge: null,
        isInflationAdjusted: false,
        survivorBenefit: null,
        inflation: null,
        isSyncAssetValueEnabled: false,
        isLiquid: options.PensionOptions.IsLiquid,
        includeInGrossEstate: null,
        customBucketIndex: null,
        orionManagementStyleId: null,
        ownerModel: [
          {
            Id: null,
            Name: options.PensionOptions.recepientOptions.People[0].Name,
            OwnerType: OwnerTypeDTO.Principal,
            Percentage: '100',
          },
        ],
      };

export const formToBody = (values: PensionFormValues): PensionFormDTO => ({
  Name: values.name,
  Recipient: values.recipient || '',
  PensionPlanType: values.type,
  LumpSumAmount: values.lumpSumAmount,
  MonthlyPayment: values.monthlyPayment,
  DistributionAge: values.distributionAge === 'At Retirement' ? null : values.distributionAge,
  DistributionAtRetirement: values.distributionAge === 'At Retirement',
  IsInflationAdjusted: values.isInflationAdjusted,
  SurvivorBenefit: values.survivorBenefit,
  Inflation: values.inflation,
  SyncAssetValueEnabled: values.isSyncAssetValueEnabled,
  IsLiquid: values.isLiquid,
  IncludeInGrossEstate: values.includeInGrossEstate,
  CustomBucketIndex: values.customBucketIndex,
  OrionManagementStyleId: values.orionManagementStyleId,
  OwnerModel: values.ownerModel
    .filter(b => b.Name && b.Percentage)
    .map(owner => ({
      ...owner,
      Id: owner.Id ?? null,
      Name: owner.Name ?? '',
      OwnerType: owner.OwnerType ?? 0,
      Percentage: Number((parseFloat(owner.Percentage ?? '0') / 100).toFixed(4)),
    })),
});

export const FORM_SCHEMA = yup.object().shape({
  type: yup
    .number()
    .nullable()
    .required('Please specify the pension plan type'),
  lumpSumAmount: yup
    .number()
    .nullable()
    .when('type', {
      is: PensionPlanTypeEnum.LumpSum,
      then: yup
        .number()
        .nullable()
        .notOneOf([0], 'Please specify a lump sum amount greater than zero')
        .required('Please specify a lump sum amount'),
    }),
  monthlyPayment: yup
    .number()
    .nullable()
    .when('type', {
      is: PensionPlanTypeEnum.LumpSum,
      otherwise: yup
        .number()
        .nullable()
        .notOneOf([0], 'Please specify a payment amount greater than zero')
        .required('Please specify a payment amount'),
    }),
});

export const LUMP_SUM_WARNING =
  'Changing your lump sum amount will affect your distribution age. Please confirm your distribution age is correct before saving.';

export const MONTHLY_PAYMENT_WARNING =
  'Changing your monthly payment amount will affect your distribution age. Please confirm your distribution age is correct before saving. Changing your pension value will automatically un-sync pension value for this account. To re-sync, toggle the sync pension value toggle in the advanced section.';

export const DISTRIBUTION_AGE_WARNING =
  'Changing your distribution age will affect your monthly payment amount. Please confirm your monthly payment value is correct before saving.';
